import { useEffect, useMemo, useRef, useState } from "react";
import type { VideoItem } from "@/mocks/videos";
import { parseDuration } from "@/utils/video";

interface DemoPlayerProps {
  video: VideoItem;
}

function toTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export default function DemoPlayer({ video }: DemoPlayerProps) {
  const total = useMemo(() => parseDuration(video.duration), [video.duration]);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [volume, setVolume] = useState(70);
  const [muted, setMuted] = useState(false);
  const [failed, setFailed] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setFailed(false);
  }, [video.id]);

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setCurrent((c) => {
        if (c + 1 >= total) {
          setPlaying(false);
          return total;
        }
        return c + 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [playing, total]);

  const toggle = () => {
    if (current >= total) setCurrent(0);
    setPlaying((p) => !p);
  };

  const seek = (clientX: number) => {
    const el = barRef.current;
    if (!el || total === 0) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setCurrent(Math.round(ratio * total));
  };

  const progress = total > 0 ? (current / total) * 100 : 0;

  if (failed) {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center rounded-lg border border-background-200 bg-foreground-950/90 text-center">
        <i className="ri-error-warning-line text-5xl text-primary-300" />
        <p className="mt-4 text-base font-medium text-background-50">视频加载失败，请稍后重试</p>
        <button
          type="button"
          onClick={() => setFailed(false)}
          className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-refresh-line" /> 重新加载
        </button>
      </div>
    );
  }

  return (
    <div className="group relative w-full overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
      <div className="relative aspect-video w-full">
        <img
          src={video.cover}
          alt={`${video.title} 演示播放画面`}
          className="h-full w-full object-cover object-top"
          onError={() => setFailed(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/20" />

        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-background-50/85 px-2 py-0.5 text-xs font-medium text-foreground-900">
          <i className="ri-live-line text-primary-600" /> 前端演示播放器
        </span>

        {!playing && (
          <button
            type="button"
            onClick={toggle}
            aria-label="播放"
            className="absolute inset-0 flex cursor-pointer items-center justify-center"
          >
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-500 text-4xl text-background-50 transition-transform duration-300 hover:scale-110">
              <i className="ri-play-fill ml-1" />
            </span>
          </button>
        )}

        {playing && (
          <button
            type="button"
            onClick={toggle}
            aria-label="暂停"
            className="absolute inset-0 flex cursor-pointer items-center justify-center opacity-0 transition-opacity hover:opacity-100"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/25 text-3xl text-background-50 backdrop-blur-sm">
              <i className="ri-pause-fill" />
            </span>
          </button>
        )}
      </div>

      {/* controls */}
      <div className="absolute inset-x-0 bottom-0 px-3 pb-3 md:px-4 md:pb-4">
        <div
          ref={barRef}
          onClick={(e) => seek(e.clientX)}
          className="group/bar flex h-4 cursor-pointer items-center"
        >
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-50/30">
            <div
              className="h-full rounded-full bg-primary-500 transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={toggle}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/20"
            aria-label={playing ? "暂停" : "播放"}
          >
            <i className={playing ? "ri-pause-fill text-xl" : "ri-play-fill text-xl"} />
          </button>
          <span className="text-xs tabular-nums text-background-100">
            {toTime(current)} / {video.duration}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-background-50 transition-colors hover:bg-background-50/20"
              aria-label={muted ? "取消静音" : "静音"}
            >
              <i
                className={`text-lg ${
                  muted || volume === 0 ? "ri-volume-mute-line" : "ri-volume-up-line"
                }`}
              />
            </button>
            <input
              type="range"
              min={0}
              max={100}
              value={muted ? 0 : volume}
              onChange={(e) => {
                setVolume(Number(e.target.value));
                setMuted(false);
              }}
              className="hidden h-1 w-24 cursor-pointer accent-primary-500 sm:block"
              aria-label="音量"
            />
          </div>
        </div>
      </div>
    </div>
  );
}